import { format, isValid } from 'date-fns';
import ReactMarkdown from 'react-markdown';

import ChartIcon from 'assets/icon-chart.png';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    chartIcon: {
        width: '16px',
        height: '16px'
    },
    date: {
        fontWeight: '800'
    },
    debitIcon: {
        padding: '0 4px'
    },
    memberHistoryBody: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0
    },
    memberHistoryItem: {
        display: 'flex',
        flexDirection: 'column',
        color: '#1F2152'
    },
    iconAction: {
        color: '#FFFFFF',
        backgroundColor: '#062D7A'
    },
    iconMinus: {
        color: '#A80A2D',
        backgroundColor: '#FEE7EC'
    },
    iconPlus: {
        color: '#1B9757',
        backgroundColor: '#E9FBF2'
    },
    iconStatus: {
        color: '#062D7A',
        backgroundColor: '#E7EFFE'
    },
    itemAmount: {
        flexShrink: 0,
        fontSize: '20px',
        fontWeight: '800',
        marginLeft: '16px',
        whiteSpace: 'nowrap'
    },
    itemActor: {
        color: '#8A96A8',
        fontFamily: 'Manrope, sans-serif',
        fontSize: 'calc(1em - 2px)',
        fontWeight: 500,
        letterSpacing: '0.35px',
        lineHeight: 'normal',
        minWidth: 0,
        overflowWrap: 'break-word'
    },
    itemActorEmphasis: {
        color: '#5A6A81',
        fontWeight: 600
    },
    itemDescription: {
        color: '#5A6A81',
        minWidth: 0,
        overflowWrap: 'break-word',
        '& p': {
            margin: 0
        }
    },
    itemDetails: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        gap: '4px',
        minWidth: 0
    },
    itemDivider: {
        borderLeft: '2px solid #E5E5E5',
        height: '48px',
        margin: '4px 0 4px 24px'
    },
    itemIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 40px',
        fontSize: '18px',
        width: '40px',
        height: '40px',
        minWidth: '40px',
        minHeight: '40px',
        marginLeft: '6px',
        borderRadius: '50%',
        aspectRatio: '1'
    },
    itemLeft: {
        display: 'flex',
        alignItems: 'center',
        flex: '1 1 auto',
        gap: '40px',
        minWidth: 0
    },
    itemType: {
        display: 'block',
        fontSize: '20px',
        fontWeight: '800',
        minWidth: 0,
        overflowWrap: 'break-word'
    }
}));

type MemberHistoryItemProps = {
    history: any[];
    item: any;
    totalIndex: number;
};

const MEMBER_HISTORY_TYPES = [
    'action_step_created',
    'action_step_completed',
    'action_step_bonus_earned',
    'general_donation',
    'redemption',
    'debit_transfer',
    'auto_debit_transfer',
    'new_status_captured',
    'give_card_assign',
    'give_card_replacement',
    'pex_card_assign',
    'pex_card_replacement'
] as const;

type MemberHistoryType = (typeof MEMBER_HISTORY_TYPES)[number];

export const isRecognizedMemberHistoryType = (
    type: string
): type is MemberHistoryType =>
    (MEMBER_HISTORY_TYPES as readonly string[]).includes(type);

type TypeMappings = Record<
    MemberHistoryType,
    {
        icon: string;
        iconClass: string;
        title: JSX.Element | string;
        amount: string;
        description: JSX.Element | string;
    }
>;

type HistoryClasses = ReturnType<typeof useStyles>;

const TypeIcon = ({
    classes,
    icon,
    iconClass,
    type
}: {
    classes: HistoryClasses;
    icon: string;
    iconClass: string;
    type: MemberHistoryType;
}) => (
    <div className={`${classes.itemIcon} ${iconClass}`}>
        {type === 'new_status_captured' ? (
            <img
                className={classes.chartIcon}
                src={ChartIcon}
                alt="chart icon"
            />
        ) : (
            <i className={icon} />
        )}
    </div>
);

const ItemDescription = ({
    classes,
    description,
    type
}: {
    classes: HistoryClasses;
    description: JSX.Element | string;
    type: MemberHistoryType;
}) => {
    if (!description) {
        return null;
    }

    return (
        <span className={classes.itemDescription}>
            {type === 'action_step_created' ? (
                <ReactMarkdown children={description as string} />
            ) : (
                description
            )}
        </span>
    );
};

const HIDDEN_ACTOR_TYPES = ['general_donation', 'action_step_bonus_earned'];

const resolveActor = (
    type: MemberHistoryType,
    actor?: {
        name?: string | null;
        organization_name?: string | null;
    } | null
) => {
    if (type === 'auto_debit_transfer') {
        return {
            actorName: 'Auto Transfer',
            organizationName: '',
            showBy: false
        };
    }

    if (HIDDEN_ACTOR_TYPES.includes(type) || !actor) {
        return null;
    }

    if (actor.name?.toLowerCase().includes('devops')) {
        return { actorName: 'Samaritan Team', organizationName: '', showBy: true };
    }

    return {
        actorName: actor.name || '',
        organizationName: actor.organization_name || '',
        showBy: true
    };
};

const ActorLine = ({
    actorName,
    classes,
    organizationName,
    showBy,
    time
}: {
    actorName: string;
    classes: HistoryClasses;
    organizationName: string;
    showBy: boolean;
    time: string;
}) => {
    if (!time && !actorName && !organizationName) {
        return null;
    }

    const actorPrefix = `${time ? ' · ' : ''}${showBy ? 'by ' : ''}`;

    return (
        <span className={classes.itemActor}>
            {time && (
                <span className={classes.itemActorEmphasis}>{time}</span>
            )}
            {actorName && (
                <span>
                    {actorPrefix}
                    <span className={classes.itemActorEmphasis}>{actorName}</span>
                </span>
            )}
            {organizationName && (
                <span>
                    {time || actorName
                        ? ` at ${organizationName}`
                        : organizationName}
                </span>
            )}
        </span>
    );
};

const MemberHistoryItem = ({
    history,
    item,
    totalIndex
}: MemberHistoryItemProps) => {
    const classes = useStyles();

    const { data, type } = item;

    if (!isRecognizedMemberHistoryType(type)) {
        return null;
    }

    const titleWithCard = (label: string) => (
        <span>
            {label} <i className={`far fa-credit-card ${classes.debitIcon}`} />{' '}
            {data.last4}
        </span>
    );

    const debitTransfer = {
        icon: 'far fa-minus',
        iconClass: classes.iconMinus,
        title: titleWithCard('Transfer to Debit Card'),
        amount: `$${data.amount}`,
        description: ''
    };

    const typeMappings: TypeMappings = {
        action_step_bonus_earned: {
            icon: 'far fa-plus',
            iconClass: classes.iconPlus,
            title: 'Action Step Bonus Earned',
            amount: `+ $${data.amount}`,
            description: `New Member Balance: $${data.new_member_balance}`
        },
        action_step_created: {
            icon: 'fas fa-clipboard-list',
            iconClass: classes.iconAction,
            title: 'New Action Step Created',
            amount: '',
            description: `${data.description}`
        },
        action_step_completed: {
            icon: 'fas fa-clipboard-list',
            iconClass: classes.iconAction,
            title: 'Action Step Complete',
            amount: '',
            description: `${data.description}`
        },
        auto_debit_transfer: debitTransfer,
        debit_transfer: debitTransfer,
        general_donation: {
            icon: 'far fa-plus',
            iconClass: classes.iconPlus,
            title: 'Donation from Support Network',
            amount: `+ $${data.amount}`,
            description: `New Member Balance: $${data.new_member_balance}`
        },
        give_card_assign: {
            icon: 'far fa-credit-card',
            iconClass: classes.iconStatus,
            title: titleWithCard('GiveCard Assign'),
            amount: '',
            description: ''
        },
        give_card_replacement: {
            icon: 'far fa-credit-card',
            iconClass: classes.iconStatus,
            title: titleWithCard('New GiveCard Assigned'),
            amount: '',
            description: ''
        },
        new_status_captured: {
            icon: 'far fa-chart',
            iconClass: classes.iconStatus,
            title: `New ${data.qol_name} Status Saved`,
            amount: '',
            description: `${data.description}`
        },
        pex_card_assign: {
            icon: 'far fa-credit-card',
            iconClass: classes.iconStatus,
            title: titleWithCard('PEX Card Assign'),
            amount: '',
            description: ''
        },
        pex_card_replacement: {
            icon: 'far fa-credit-card',
            iconClass: classes.iconStatus,
            title: titleWithCard('New PEX Card Assigned'),
            amount: '',
            description: ''
        },
        redemption: {
            icon: 'far fa-minus',
            iconClass: classes.iconMinus,
            title: 'Purchase Request Submitted',
            amount: `- $${data.amount}`,
            description: `New Member Balance: $${data.new_member_balance}`
        }
    };

    const mapping = typeMappings[type];
    const createdAt = item.created_at ? new Date(item.created_at) : null;
    const time =
        createdAt && isValid(createdAt) ? format(createdAt, 'h:mm a') : '';
    const actorLine = resolveActor(type, item.actor);
    const showDivider = totalIndex !== history.length - 1;

    return (
        <div className={classes.memberHistoryItem}>
            <div className={classes.memberHistoryBody}>
                <div className={classes.itemLeft}>
                    <TypeIcon
                        classes={classes}
                        icon={mapping.icon}
                        iconClass={mapping.iconClass}
                        type={type}
                    />
                    <div className={classes.itemDetails}>
                        <span className={classes.itemType}>{mapping.title}</span>
                        <ItemDescription
                            classes={classes}
                            description={mapping.description}
                            type={type}
                        />
                        <ActorLine
                            actorName={actorLine?.actorName || ''}
                            classes={classes}
                            organizationName={actorLine?.organizationName || ''}
                            showBy={actorLine?.showBy ?? false}
                            time={time}
                        />
                    </div>
                </div>
                {mapping.amount && (
                    <span className={classes.itemAmount}>{mapping.amount}</span>
                )}
            </div>
            {showDivider && <div className={classes.itemDivider} />}
        </div>
    );
};

export default MemberHistoryItem;
